// frontend/src/pages/Plans.jsx
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getPlans, createCheckout, createCheckoutPayg, submitContact, getProfile, cancelSubscription } from '../api/services'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const TIER_ORDER = ['freemium', 'essential', 'professional', 'expert', 'power']

// Synthetic Pay-as-you-go card. PAYG isn't a row in the plans table — it's
// per-message billing — so it's defined locally and routes through its own
// one-off Checkout session (mode='payment') rather than the /plans API.
const PAYG_CARD = {
    id: '__payg__',
    _payg: true,
    display_name: 'Pay as you go',
}

// ── Custom/Enterprise Card ─────────────────────────────────────────────
const CUSTOM_CARD = {
    id: '__custom__',
    _custom: true,
    display_name: 'Enterprise',
    description: 'For organisations with higher volume needs',
    features: [
        'Custom message volume',
        'Team accounts',
        'Priority support',
        'Dedicated account manager',
    ],
}

// ── Downgrade Confirmation Modal ─────────────────────────────────────
function DowngradeConfirmModal({ isOpen, onClose, onConfirm, periodEndDate, isAlreadyCancelled }) {
    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-[#0D1117] border border-white/10 rounded-2xl p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-display font-bold text-white">
                        {isAlreadyCancelled ? 'Already Cancelled' : 'Confirm Downgrade'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-white/40 hover:text-white transition-colors text-xl"
                    >
                        ✕
                    </button>
                </div>

                <div className="space-y-4">
                    {isAlreadyCancelled ? (
                        <>
                            <div className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/25 rounded-xl">
                                <svg className="w-5 h-5 text-orange-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                <p className="text-orange-400 text-sm font-body">
                                    Your subscription is already scheduled to cancel.
                                </p>
                            </div>
                            <p className="text-white/60 text-sm font-body leading-relaxed">
                                You'll keep full access to your current plan until <strong className="text-white">{periodEndDate}</strong>.
                                After that, you'll be automatically downgraded to the Free plan.
                            </p>
                            <p className="text-white/40 text-xs font-body">
                                No further action is required from you.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-white/70 text-sm font-body">
                                Are you sure you want to downgrade to the Free plan?
                            </p>
                            <p className="text-white/40 text-xs font-body">
                                You'll keep full access to your current plan until <strong className="text-white/60">{periodEndDate}</strong>.
                                After that, you'll be downgraded to the Free plan.
                            </p>
                        </>
                    )}

                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 border border-white/10 text-white/70 hover:text-white rounded-xl text-sm font-bold transition-all"
                        >
                            {isAlreadyCancelled ? 'Close' : 'Cancel'}
                        </button>
                        {!isAlreadyCancelled && (
                            <button
                                onClick={() => {
                                    onConfirm()
                                    onClose()
                                }}
                                className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold transition-all"
                            >
                                Confirm Downgrade
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

// ── Enterprise Contact Modal ──────────────────────────────────────────
function EnterpriseModal({ isOpen, onClose, onSubmit, submitting }) {
    const [form, setForm] = useState({ name: '', email: '', company: '', message: '' })
    const [error, setError] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [ticketCode, setTicketCode] = useState(null)
    const navigate = useNavigate()

    if (!isOpen) return null

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setSubmitted(false)

        try {
            const ref = await onSubmit(form)
            setTicketCode(ref || null)
            setSubmitted(true)
            setTimeout(() => {
                setForm({ name: '', email: '', company: '', message: '' })
                onClose()
                navigate('/dashboard')
            }, 3000)
        } catch (err) {
            setError('Failed to send enquiry. Please try again or email us directly.')
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-[#0D1117] border border-white/10 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-display font-bold text-white">Contact Sales</h3>
                    <button
                        onClick={onClose}
                        className="text-white/40 hover:text-white transition-colors text-xl"
                    >
                        ✕
                    </button>
                </div>

                {submitted ? (
                    <div className="py-8 text-center">
                        <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                            style={{ backgroundColor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <p className="text-sm font-sans font-bold text-white mb-1">Thank you — enquiry received</p>
                        <p className="text-[13px] font-body text-white/50">Our team will respond within one business day.</p>
                        {ticketCode && (
                            <div className="mt-4 inline-flex flex-col items-center gap-1 px-5 py-3 rounded-xl bg-brand-amber/[0.06] border border-brand-amber/20">
                                <span className="text-[9px] font-sans font-semibold tracking-widest uppercase text-white/40">
                                    Your reference
                                </span>
                                <span className="text-lg font-mono font-bold text-brand-amber tracking-wide">
                                    {ticketCode}
                                </span>
                            </div>
                        )}
                        <p className="text-[11px] font-body text-white/30 mt-3">Redirecting to dashboard...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-[11px] font-sans font-bold tracking-wide uppercase text-white/40 mb-2">
                                Full name
                            </label>
                            <input
                                type="text" name="name" required
                                value={form.name} onChange={handleChange}
                                placeholder="Full name"
                                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-colors bg-white/5 border border-white/10 focus:border-brand-amber/40"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-sans font-bold tracking-wide uppercase text-white/40 mb-2">
                                Email address
                            </label>
                            <input
                                type="email" name="email" required
                                value={form.email} onChange={handleChange}
                                placeholder="Email address"
                                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-colors bg-white/5 border border-white/10 focus:border-brand-amber/40"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-sans font-bold tracking-wide uppercase text-white/40 mb-2">
                                Company name
                            </label>
                            <input
                                type="text" name="company" required
                                value={form.company} onChange={handleChange}
                                placeholder="Company name"
                                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-colors bg-white/5 border border-white/10 focus:border-brand-amber/40"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-sans font-bold tracking-wide uppercase text-white/40 mb-2">
                                Tell us about your team
                            </label>
                            <textarea
                                name="message" required rows={4}
                                value={form.message} onChange={handleChange}
                                placeholder="Team size, what you're hoping to achieve, any specific requirements"
                                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-colors bg-white/5 border border-white/10 focus:border-brand-amber/40 resize-none"
                            />
                        </div>

                        {error && (
                            <p className="text-[12px] font-body text-red-400 leading-relaxed">{error}</p>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-3.5 rounded-xl font-sans font-bold text-sm transition-all flex items-center justify-center gap-2 bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] disabled:opacity-50"
                        >
                            {submitting ? 'Sending...' : 'Send enquiry'}
                        </button>

                        <p className="text-[10px] text-white/30 text-center font-body">
                            We'll respond within one business day.
                        </p>
                    </form>
                )}
            </div>
        </div>
    )
}

export default function Plans() {
    const [plans, setPlans] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [redirecting, setRedirecting] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [showDowngradeModal, setShowDowngradeModal] = useState(false)
    const [currentUserPlan, setCurrentUserPlan] = useState(null)
    const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null)
    const [pendingCancellation, setPendingCancellation] = useState(false)
    const [isAlreadyCancelled, setIsAlreadyCancelled] = useState(false)
    const navigate = useNavigate()

    // Fetch the current user's plan from the backend
    const fetchUserPlan = async () => {
        try {
            const token = localStorage.getItem('gmc_token')
            if (!token) {
                setCurrentUserPlan('freemium')
                setPendingCancellation(false)
                setCurrentPeriodEnd(null)
                setIsAlreadyCancelled(false)
                return
            }
            const response = await getProfile()
            const plan = response.data.plan_tier || 'freemium'
            setCurrentUserPlan(plan)

            // Check if there's a pending cancellation
            const isPending = response.data.cancel_at_period_end || false
            setPendingCancellation(isPending)
            setCurrentPeriodEnd(response.data.current_period_end || null)

            // Check if subscription status is already cancelled
            const status = response.data.subscription_status || ''
            const isCancelled = status === 'cancelled' || status === 'canceled'
            setIsAlreadyCancelled(isCancelled)

            // Also update localStorage to keep it in sync
            const user = JSON.parse(localStorage.getItem('gmc_user') || '{}')
            user.plan_tier = plan
            user.cancel_at_period_end = isPending
            user.current_period_end = response.data.current_period_end || null
            user.subscription_status = status
            localStorage.setItem('gmc_user', JSON.stringify(user))
        } catch {
            setCurrentUserPlan('freemium')
            setPendingCancellation(false)
            setCurrentPeriodEnd(null)
            setIsAlreadyCancelled(false)
        }
    }

    useEffect(() => {
        // First try to get from localStorage, then fetch from backend
        const localUser = JSON.parse(localStorage.getItem('gmc_user') || '{}')
        if (localUser.plan_tier) {
            setCurrentUserPlan(localUser.plan_tier)
            setPendingCancellation(localUser.cancel_at_period_end || false)
            setCurrentPeriodEnd(localUser.current_period_end || null)
            setIsAlreadyCancelled(localUser.subscription_status === 'cancelled' || localUser.subscription_status === 'canceled')
        }
        // Always fetch fresh from backend
        fetchUserPlan()
    }, [])

    useEffect(() => {
        getPlans()
            .then((res) => {
                const sorted = [...res.data].sort(
                    (a, b) =>
                        TIER_ORDER.indexOf(a.name.toLowerCase()) -
                        TIER_ORDER.indexOf(b.name.toLowerCase())
                )
                setPlans(sorted)
            })
            .catch(() => setError('Failed to load plans. Is the backend running?'))
            .finally(() => setLoading(false))
    }, [])

    const formatPrice = (pricePence) => {
        if (!pricePence) return null
        return (pricePence / 100).toFixed(2)
    }

    const goToCheckout = async (key, request) => {
        try {
            setError('')
            setRedirecting(key)
            const res = await request()
            const url = res.data?.checkout_url
            if (!url) throw new Error('No checkout URL returned')
            window.location.href = url
        } catch (err) {
            setRedirecting(null)
            if (err?.response?.status === 401) {
                navigate('/login', { state: { from: '/plans' } })
                return
            }
            setError('Could not start checkout. Please try again.')
        }
    }

    // ── Cancel subscription at period end ─────────────────────────────
    const cancelSubscriptionAtPeriodEnd = async () => {
        try {
            const response = await cancelSubscription()
            const data = response.data

            setPendingCancellation(true)
            setCurrentPeriodEnd(data.current_period_end)

            // Check if already cancelled
            if (data.already_cancelled) {
                setIsAlreadyCancelled(true)
                const periodEndDate = data.current_period_end
                    ? new Date(data.current_period_end).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    })
                    : 'the end of your billing period'

                // Update localStorage
                const user = JSON.parse(localStorage.getItem('gmc_user') || '{}')
                user.cancel_at_period_end = true
                user.current_period_end = data.current_period_end
                user.subscription_status = 'cancelled'
                localStorage.setItem('gmc_user', JSON.stringify(user))

                navigate('/dashboard', {
                    state: {
                        message: `Your plan is already set to downgrade to Free on ${periodEndDate}. You'll keep full access until then.`
                    }
                })
                return
            }

            // Update localStorage
            const user = JSON.parse(localStorage.getItem('gmc_user') || '{}')
            user.cancel_at_period_end = true
            user.current_period_end = data.current_period_end
            localStorage.setItem('gmc_user', JSON.stringify(user))

            // Format the date for display
            const periodEndDate = data.current_period_end
                ? new Date(data.current_period_end).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                })
                : 'the end of your billing period'

            navigate('/dashboard', {
                state: {
                    message: `Your plan will downgrade to Free on ${periodEndDate}. You'll keep full access until then.`
                }
            })
        } catch (err) {
            // Any 409 on this endpoint is benign: it means there is nothing to
            // cancel (already on Free, already cancelled, or no active Stripe
            // subscription). Never surface a scary failure for a no-op — reflect
            // the true state and refresh from the backend.
            if (err?.response?.status === 409) {
                await fetchUserPlan()
                navigate('/dashboard', {
                    state: {
                        message: "You're already on the Free plan, or your subscription is already set to cancel. No changes were needed."
                    }
                })
                return
            }
            // Genuine failure (network / 5xx) — this is the only case that warrants
            // an error the user should retry.
            setError('We could not process that request just now. Please try again in a moment.')
        }
    }

    const handleDowngrade = async () => {
        await cancelSubscriptionAtPeriodEnd()
    }

    const handleSelect = (plan) => {
        const tier = plan.name.toLowerCase()
        const isCurrentPlan = tier === currentUserPlan

        if (isCurrentPlan) {
            return
        }

        // If switching to freemium from a paid plan → show downgrade confirmation
        if (tier === 'freemium' && currentUserPlan && currentUserPlan !== 'freemium') {
            // Check if there's already a pending cancellation or already cancelled
            if (pendingCancellation || isAlreadyCancelled) {
                // Show the modal with "already cancelled" state
                setShowDowngradeModal(true)
                return
            }
            setShowDowngradeModal(true)
            return
        }

        if (tier === 'freemium') {
            navigate('/analyze')
            return
        }

        // Paid plan → go to checkout (upgrade or switch)
        goToCheckout(tier, () => createCheckout(tier))
    }

    const handlePayg = (productType) =>
        goToCheckout(productType, () => createCheckoutPayg(productType))

    const handleEnterpriseSubmit = async (form) => {
        setSubmitting(true)
        try {
            const res = await submitContact({ ...form, category: 'enterprise' })
            return res.data?.ticket_code || null
        } finally {
            setSubmitting(false)
        }
    }

    // Format period end date for display
    const formatPeriodEnd = (dateStr) => {
        if (!dateStr) return 'the end of your billing period'
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            })
        } catch {
            return 'the end of your billing period'
        }
    }

    // Check if the user can downgrade (not already cancelled)
    const canDowngrade = !isAlreadyCancelled && !pendingCancellation

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0D1117] flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <p className="text-white/40 font-body text-sm tracking-widest uppercase animate-pulse">
                        Loading plans…
                    </p>
                </div>
                <Footer />
            </div>
        )
    }

    const cards = (() => {
        const list = [...plans]
        const freemiumIdx = list.findIndex((p) => p.name?.toLowerCase() === 'freemium')
        list.splice(freemiumIdx >= 0 ? freemiumIdx + 1 : list.length, 0, PAYG_CARD)
        list.push(CUSTOM_CARD)
        return list
    })()

    const isLoggedIn = !!localStorage.getItem('gmc_token')

    return (
        <div className="min-h-screen bg-[#0D1117] flex flex-col">
            <Navbar />
            <div className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">

                <div className="mb-10">
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
                        Choose your plan
                    </h1>
                    <p className="text-white/50 font-body text-sm">
                        All plans include full risk assessment. Pay per message or subscribe for volume.
                    </p>
                </div>

                {error && (
                    <div className="border border-red-500/30 bg-red-500/10 rounded-xl px-4 py-3 mb-8">
                        <p className="text-red-400 text-sm font-body">{error}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 pt-4">
                    {cards.map((plan) => {
                        if (plan._custom) {
                            return (
                                <div
                                    key={plan.id}
                                    className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col relative transition-all duration-300 hover:border-brand-amber/50 hover:bg-white/[0.07]"
                                >
                                    <div className="mb-4">
                                        <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                            {plan.display_name}
                                        </p>
                                        <div className="flex items-baseline gap-1">
                                            <span className="font-display text-3xl font-bold text-brand-amber">
                                                Contact Sales
                                            </span>
                                        </div>
                                        <p className="text-white/40 text-xs font-body mt-1">
                                            {plan.description}
                                        </p>
                                    </div>

                                    <div className="flex-1 space-y-2 mb-6">
                                        {plan.features.map((feature, i) => (
                                            <div key={i} className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                                <span className="text-white/75 text-sm font-body">{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => setModalOpen(true)}
                                        className="w-full text-center px-6 py-3 border border-brand-amber/40 text-brand-amber hover:bg-brand-amber hover:text-[#0D1117] rounded-xl text-sm font-bold transition-all"
                                    >
                                        Contact Sales
                                    </button>
                                </div>
                            )
                        }

                        if (plan._payg) {
                            const busy = redirecting === 'diagnose_only' || redirecting === 'diagnose_rewrite'
                            return (
                                <div
                                    key={plan.id}
                                    className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col relative transition-all duration-300 hover:border-brand-amber/50"
                                >
                                    <div className="mb-4">
                                        <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                            {plan.display_name}
                                        </p>
                                        <div className="flex items-baseline gap-1">
                                            <span className="font-display text-3xl font-bold text-white">From £0.99</span>
                                            <span className="text-white/40 text-sm font-body">/message</span>
                                        </div>
                                    </div>

                                    <div className="flex-1 space-y-2 mb-6">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                            <span className="text-white/75 text-sm font-body">No subscription — pay per use</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                            <span className="text-white/75 text-sm font-body">Full risk assessment</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                            <span className="text-white/75 text-sm font-body">Zero message retention</span>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <button
                                            onClick={() => handlePayg('diagnose_only')}
                                            disabled={busy}
                                            className={`w-full text-center px-6 py-3 rounded-xl text-sm font-bold transition-all border border-brand-amber/40 text-brand-amber hover:bg-brand-amber hover:text-[#0D1117] hover:border-brand-amber ${busy ? 'opacity-60 cursor-wait' : ''}`}
                                        >
                                            {redirecting === 'diagnose_only' ? 'Redirecting…' : 'Diagnose · £0.99'}
                                        </button>
                                        <button
                                            onClick={() => handlePayg('diagnose_rewrite')}
                                            disabled={busy}
                                            className={`w-full text-center px-6 py-3 rounded-xl text-sm font-bold transition-all border border-brand-amber/40 text-brand-amber hover:bg-brand-amber hover:text-[#0D1117] hover:border-brand-amber ${busy ? 'opacity-60 cursor-wait' : ''}`}
                                        >
                                            {redirecting === 'diagnose_rewrite' ? 'Redirecting…' : 'Diagnose + rewrite · £1.99'}
                                        </button>
                                    </div>
                                </div>
                            )
                        }

                        const tier = plan.name.toLowerCase()
                        const isPopular = tier === 'professional'
                        const isFree = tier === 'freemium'
                        const price = formatPrice(plan.price_pence)
                        const isRedirecting = redirecting === tier
                        const isCurrentPlan = tier === currentUserPlan

                        let buttonText = 'Select plan'
                        let isDisabled = false
                        let buttonClass = ''

                        if (isCurrentPlan) {
                            if (pendingCancellation || isAlreadyCancelled) {
                                // Premium framing: emphasise the access the user KEEPS
                                // until period end, not the loss. Reassuring + factual.
                                buttonText = `Active until ${formatPeriodEnd(currentPeriodEnd)}`
                                isDisabled = true
                                buttonClass = 'bg-white/5 text-white/40 cursor-not-allowed border border-brand-amber/25'
                            } else {
                                buttonText = 'Current Plan'
                                isDisabled = true
                                buttonClass = 'bg-emerald-500/25 text-emerald-300 border border-emerald-400/60 cursor-not-allowed shadow-[0_0_18px_-4px_rgba(16,185,129,0.5)]'
                            }
                        } else if (isFree && isLoggedIn && currentUserPlan && currentUserPlan !== 'freemium') {
                            // ✅ Premium UX: a paid user sees a downgrade control only when
                            // there is genuinely something to downgrade. If cancellation is
                            // already pending or the subscription is already cancelled, the
                            // button is disabled and clearly labelled — never clickable into
                            // a no-op that would surface an error.
                            if (pendingCancellation || isAlreadyCancelled) {
                                // Freemium is the plan the user is moving TO — it is
                                // scheduled to begin at period end, not being cancelled.
                                // Label it as incoming, never "cancellation".
                                buttonText = `Starts ${formatPeriodEnd(currentPeriodEnd)}`
                                isDisabled = true
                                buttonClass = 'bg-white/5 text-white/40 cursor-not-allowed border border-brand-amber/25'
                            } else {
                                buttonText = 'Downgrade to Free'
                                buttonClass = 'border border-white/20 text-white/60 hover:text-white hover:bg-white/5 hover:border-white/40'
                            }
                        } else if (isFree) {
                            buttonText = 'Start free'
                            buttonClass = 'border border-brand-amber/35 text-brand-amber hover:bg-brand-amber hover:text-[#0D1117] hover:border-brand-amber'
                        } else if (isRedirecting) {
                            buttonText = 'Redirecting…'
                            isDisabled = true
                        } else if (isPopular) {
                            buttonText = `Choose ${plan.display_name}`
                            buttonClass = 'bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] shadow-[0_4px_18px_-4px_rgba(212,168,67,0.5)]'
                        } else {
                            buttonText = `Choose ${plan.display_name}`
                            buttonClass = 'border border-brand-amber/35 text-brand-amber hover:bg-brand-amber hover:text-[#0D1117] hover:border-brand-amber'
                        }

                        let badge = null
                        // Current-plan state is now shown by the green button + green card
                        // outline, and (when cancelling) by an in-card line under the title —
                        // not by an outline badge. Only "Most popular" rides the outline.
                        if (isPopular && !isCurrentPlan) {
                            badge = {
                                text: 'Most popular',
                                glyph: '✦',
                                className: 'bg-brand-amber text-[#0D1117] shadow-[0_3px_14px_-3px_rgba(212,168,67,0.7)]',
                            }
                        }

                        // Check if this plan is currently active but has pending cancellation
                        const isPendingCancel = isCurrentPlan && (pendingCancellation || isAlreadyCancelled)

                        // Popular gets the premium treatment (lift + glow) only when it is
                        // NOT the user's current plan — current-plan green styling wins to
                        // avoid a green/amber clash (Option A).
                        const showPopularTreatment = isPopular && !isCurrentPlan

                        return (
                            <div
                                key={plan.id}
                                className={`bg-white/[0.03] border rounded-2xl p-7 flex flex-col relative transition-all duration-300 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.8)] hover:border-brand-amber/50 hover:bg-white/[0.05] hover:shadow-[0_10px_34px_-12px_rgba(0,0,0,0.9)] hover:-translate-y-1
                                    ${isPopular ? 'border-brand-amber/60' : 'border-white/10'}
                                    ${isPopular ? 'bg-brand-amber/[0.06]' : ''}
                                    ${showPopularTreatment ? 'md:-translate-y-3 shadow-[0_10px_44px_-10px_rgba(212,168,67,0.4)]' : ''}
                                    ${isCurrentPlan && !isPendingCancel ? 'ring-2 ring-emerald-400/70 border-emerald-400/60 shadow-[0_0_36px_-12px_rgba(16,185,129,0.55)]' : ''}
                                    ${isPendingCancel ? 'ring-2 ring-brand-amber/50' : ''}
                                `}
                            >
                                {badge && (
                                    <div className="absolute -top-3 left-0 right-0 flex justify-center">
                                        <span className={`inline-flex items-center gap-1 text-xs font-body font-semibold px-4 py-1.5 rounded-full tracking-[0.08em] whitespace-nowrap shadow-md ${badge.className}`}>
                                            {badge.glyph && <span className="text-xs leading-none">{badge.glyph}</span>}
                                            {badge.text}
                                        </span>
                                    </div>
                                )}

                                <div className="mb-4">
                                    <p className="text-brand-amber text-sm font-body font-semibold uppercase tracking-widest mb-1.5">
                                        {plan.display_name}
                                    </p>
                                    {isPendingCancel && (
                                        <p className="flex items-center gap-1.5 text-brand-amber text-sm font-body font-medium mb-1.5">
                                            <span className="w-2 h-2 rounded-full bg-brand-amber animate-pulse" />
                                            Active until {formatPeriodEnd(currentPeriodEnd)}
                                        </p>
                                    )}
                                    <div className="flex items-baseline gap-1">
                                        {isFree ? (
                                            <span className="font-display text-4xl font-bold text-brand-amber">Free</span>
                                        ) : price ? (
                                            <>
                                                <span className="font-display text-4xl font-bold text-brand-amber">
                                                    £{price}
                                                </span>
                                                <span className="text-white/50 text-base font-body">/mo</span>
                                            </>
                                        ) : (
                                            <span className="font-display text-4xl font-bold text-brand-amber">PAYG</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex-1 space-y-2 mb-6">
                                    {plan.monthly_cap ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                            <span className="text-white/75 text-sm font-body">
                                                {plan.monthly_cap === -1
                                                    ? 'Unlimited Message Analyses'
                                                    : `${plan.monthly_cap} Message Analyses/mo`}
                                            </span>
                                        </div>
                                    ) : null}
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                        <span className="text-white/75 text-sm font-body">
                                            {plan.rewrite_cap === -1
                                                ? 'Unlimited Professional Rewrites'
                                                : plan.rewrite_cap > 0
                                                    ? `${plan.rewrite_cap} Professional Rewrites/mo`
                                                    : 'No rewrites included'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                        <span className="text-white/75 text-sm font-body">Full risk assessment</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                        <span className="text-white/75 text-sm font-body">Zero message retention</span>
                                    </div>
                                    {!isFree && (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-brand-amber shrink-0" />
                                            <span className="text-white/75 text-sm font-body">Priority processing</span>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => handleSelect(plan)}
                                    disabled={isDisabled || isRedirecting}
                                    className={`w-full text-center px-6 py-3 rounded-xl text-sm font-bold transition-all
                                        ${buttonClass}
                                        ${isRedirecting ? 'opacity-60 cursor-wait' : ''}
                                    `}
                                >
                                    {buttonText}
                                </button>
                            </div>
                        )
                    })}
                </div>

                <EnterpriseModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onSubmit={handleEnterpriseSubmit}
                    submitting={submitting}
                />

                <DowngradeConfirmModal
                    isOpen={showDowngradeModal}
                    onClose={() => setShowDowngradeModal(false)}
                    onConfirm={handleDowngrade}
                    periodEndDate={currentPeriodEnd ? formatPeriodEnd(currentPeriodEnd) : 'the end of your billing period'}
                    isAlreadyCancelled={pendingCancellation || isAlreadyCancelled}
                />
            </div>
            <Footer />
        </div>
    )
}